var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import classNames from "classnames";
import { KAKAO_AUTH_STRATEGY } from "@jti/fe-common";
import { useAuthenticationMethods } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { CircleLoader, Icon, useCheckBFCache, useConfig, useWindowSearchQuery } from "@jti/ui";
import DialogIcon from "@jti/ui/icons/dialog.svg";
import ROUTES_PATHS from "../../../constants/routes";
import { useKakao } from "../../../hooks/useKakao/useKakao";
import styles from "./KakaoLogin.module.scss";
export const KakaoLogin = ({ kakaoSdkLink, integrityKey, sdkKey, buttonTitle, redirectUrlAfterLogin, isLoginInProcess, isLoginFailed, isLoginSuccess, loginUser, }) => {
    const config = useConfig();
    const { code } = useWindowSearchQuery();
    const { isScriptInitiated } = useKakao({
        kakaoSdkLink,
        integrityKey,
        sdkKey,
    });
    const history = useHistory();
    const { isPageFromCache } = useCheckBFCache();
    const { data: availableAuthMethods, isLoading } = useAuthenticationMethods();
    // The flag to show when actual Kakao login is in progress
    const [isKakaoAuthPending, setIsKakaoAuthPending] = useState(false);
    const kakaoServiceTerms = useMemo(() => { var _a; return (_a = availableAuthMethods === null || availableAuthMethods === void 0 ? void 0 : availableAuthMethods[KAKAO_AUTH_STRATEGY]) === null || _a === void 0 ? void 0 : _a.options["service_terms"]; }, [availableAuthMethods]);
    useEffect(() => {
        if (isPageFromCache) {
            // If page was restored from the back-forward-cache,
            // remove the pending state from the button
            setIsKakaoAuthPending(false);
        }
    }, [isPageFromCache]);
    const onKakaoLoginClick = () => {
        var _a;
        setIsKakaoAuthPending(true);
        dispatchTrackingEvent(TrackingEvents.TrackingLoginKakao);
        (_a = window.Kakao) === null || _a === void 0 ? void 0 : _a.Auth.authorize(Object.assign({ redirectUri: redirectUrlAfterLogin, throughTalk: config.isLoginThroughKakaoTalkEnabled }, (kakaoServiceTerms && { serviceTerms: kakaoServiceTerms })));
    };
    const shouldCallLogin = isLoginFailed === false && isLoginInProcess === false && isLoginSuccess === false;
    const login = useCallback((token) => __awaiter(void 0, void 0, void 0, function* () {
        setIsKakaoAuthPending(true);
        try {
            const loginResult = yield loginUser({ strategy: "kakao-credential", strategy_data: { code: token } });
            if (loginResult === null || loginResult === void 0 ? void 0 : loginResult.isError) {
                history.push({ search: `?flow=kakao`, pathname: ROUTES_PATHS.register });
            }
        }
        finally {
            setIsKakaoAuthPending(false);
        }
    }), [history, loginUser]);
    useEffect(() => {
        if (typeof code === "string" && shouldCallLogin) {
            login(code);
        }
    }, [code, login, shouldCallLogin]);
    return (_jsxs("button", { onClick: onKakaoLoginClick, "data-testid": "kakaoLoginButton", type: "button", className: classNames(styles.kakaoButton, { [styles.pending]: isKakaoAuthPending }), disabled: !isScriptInitiated || isLoading || isLoginInProcess || isKakaoAuthPending, children: [_jsx(Icon, { component: DialogIcon, className: styles.icon }), _jsx("span", { className: styles.loginText, children: buttonTitle }), isKakaoAuthPending ? (_jsx(CircleLoader, { className: styles.buttonLoader, dataTestId: "buttonLoader", width: 22 })) : null] }));
};
